import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { UserWithRole } from '../../../../../shared/services/userService';
import usePageTranslation from '../../../../../shared/hooks/usePageTranslation';

interface SanctionModalProps {
  user: UserWithRole;
  updating: boolean;
  onSanction: (type: 'ban_permanent' | 'ban_temporary' | 'mute' | 'unban' | 'unmute', duration?: number, reason?: string) => void;
  onCancel: () => void;
  currentUserRoleId?: number;
  context?: 'roles' | 'users';
}

const SanctionModal: React.FC<SanctionModalProps> = ({
  user,
  updating,
  onSanction,
  onCancel,
  currentUserRoleId,
}) => {
  const { t } = usePageTranslation('panel', 'UserRolesManagement');
  const [sanctionType, setSanctionType] = useState<'ban_permanent' | 'ban_temporary' | 'mute' | 'unban' | 'unmute'>('ban_temporary');
  const [duration, setDuration] = useState<number>(1);
  const [durationUnit, setDurationUnit] = useState<'hours' | 'days' | 'weeks' | 'months'>('days');
  const [reason, setReason] = useState('');

  const isCurrentlyBanned = user.bann && user.bann > 0;
  const isCurrentlyMuted = user.muted;
  const canBanPermanent = currentUserRoleId && currentUserRoleId <= 2;

  useEffect(() => {
    if (!canBanPermanent && sanctionType === 'ban_permanent') {
      setSanctionType('ban_temporary');
    }
  }, [canBanPermanent, sanctionType]);

  const handleConfirm = () => {
    let durationInHours = 0;
    if (sanctionType === 'ban_temporary' || sanctionType === 'mute') {
      switch (durationUnit) {
        case 'hours': durationInHours = duration; break;
        case 'days': durationInHours = duration * 24; break;
        case 'weeks': durationInHours = duration * 24 * 7; break;
        case 'months': durationInHours = duration * 24 * 30; break;
      }
    }
    onSanction(sanctionType, durationInHours > 0 ? durationInHours : undefined, reason || undefined);
  };

  const canApplySanction = () => {
    if (sanctionType === 'unban' && !isCurrentlyBanned) return false;
    if (sanctionType === 'unmute' && !isCurrentlyMuted) return false;
    if ((sanctionType === 'ban_temporary' || sanctionType === 'mute') && (!duration || duration <= 0)) return false;
    return true;
  };

  const btnActive = (color: string) => `p-3 rounded-lg border text-left transition-colors bg-${color}-500/20 text-${color}-300 border-${color}-500/40`;
  const btnInactive = 'p-3 rounded-lg border text-left transition-colors border-[#2b3a61] bg-[#0d1b3a]/80 text-slate-300 hover:bg-[#0d1b3a]';

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="rounded-xl border border-[#243052]/90 bg-[#0a1128] p-6 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-white mb-5">
          {t('sanction.title')} — {user.username}
        </h3>

        <div className="mb-5 rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-4">
          <h4 className="text-sm font-semibold text-slate-200 mb-2">{t('sanction.currentStatus')}</h4>
          <div className="space-y-2">
            {isCurrentlyBanned ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-300 border border-red-500/40">
                  {user.ban_end ? t('sanction.bannedTemporary') : t('sanction.bannedPermanent')}
                </span>
                {user.ban_end && <span className="text-slate-400 text-xs">{t('sanction.until')} {new Date(user.ban_end).toLocaleString('fr-FR')}</span>}
                {user.ban_reason && <span className="text-slate-400 text-xs">— {user.ban_reason}</span>}
              </div>
            ) : (
              <span className="px-2 py-1 rounded text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                {t('sanction.notBanned')}
              </span>
            )}
            {isCurrentlyMuted ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-1 rounded text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-500/40">
                  {t('sanction.muted')}
                </span>
                {user.mute_end && <span className="text-slate-400 text-xs">{t('sanction.until')} {new Date(user.mute_end).toLocaleString('fr-FR')}</span>}
                {user.mute_reason && <span className="text-slate-400 text-xs">— {user.mute_reason}</span>}
              </div>
            ) : (
              <span className="px-2 py-1 rounded text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                {t('sanction.notMuted')}
              </span>
            )}
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-xs font-medium text-slate-400 mb-2">{t('sanction.selectType')}</label>
          <div className="grid grid-cols-2 gap-3">
            {!isCurrentlyBanned && (
              <>
                {canBanPermanent && (
                  <button
                    onClick={() => setSanctionType('ban_permanent')}
                    className={sanctionType === 'ban_permanent' ? btnActive('red') : btnInactive}
                  >
                    <div className="font-semibold text-sm">{t('sanction.banPermanent')}</div>
                    <div className="text-xs text-slate-400 mt-1">{t('sanction.banPermanentDesc')}</div>
                  </button>
                )}
                <button
                  onClick={() => setSanctionType('ban_temporary')}
                  className={sanctionType === 'ban_temporary' ? btnActive('orange') : btnInactive}
                >
                  <div className="font-semibold text-sm">{t('sanction.banTemporary')}</div>
                  <div className="text-xs text-slate-400 mt-1">{t('sanction.banTemporaryDesc')}</div>
                </button>
              </>
            )}
            {isCurrentlyBanned && (
              <button
                onClick={() => setSanctionType('unban')}
                className={sanctionType === 'unban' ? btnActive('emerald') : btnInactive}
              >
                <div className="font-semibold text-sm">{t('sanction.unban')}</div>
                <div className="text-xs text-slate-400 mt-1">{t('sanction.unbanDesc')}</div>
              </button>
            )}
            {!isCurrentlyMuted ? (
              <button
                onClick={() => setSanctionType('mute')}
                className={sanctionType === 'mute' ? btnActive('amber') : btnInactive}
              >
                <div className="font-semibold text-sm">{t('sanction.mute')}</div>
                <div className="text-xs text-slate-400 mt-1">{t('sanction.muteDesc')}</div>
              </button>
            ) : (
              <button
                onClick={() => setSanctionType('unmute')}
                className={sanctionType === 'unmute' ? btnActive('emerald') : btnInactive}
              >
                <div className="font-semibold text-sm">{t('sanction.unmute')}</div>
                <div className="text-xs text-slate-400 mt-1">{t('sanction.unmuteDesc')}</div>
              </button>
            )}
          </div>
        </div>

        {(sanctionType === 'ban_temporary' || sanctionType === 'mute') && (
          <div className="mb-5">
            <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('sanction.duration')}</label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="1"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
                className="w-20 rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#3d4fdb]/60"
              />
              <select
                value={durationUnit}
                onChange={(e) => setDurationUnit(e.target.value as 'hours' | 'days' | 'weeks' | 'months')}
                className="rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#3d4fdb]/60"
              >
                <option value="hours">{t('sanction.hours')}</option>
                <option value="days">{t('sanction.days')}</option>
                <option value="weeks">{t('sanction.weeks')}</option>
                <option value="months">{t('sanction.months')}</option>
              </select>
            </div>
          </div>
        )}

        <div className="mb-5">
          <label className="block text-xs font-medium text-slate-400 mb-1.5">
            {t('sanction.reason')} ({t('modal.changeRole.optional')})
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('sanction.reasonPlaceholder')}
            rows={3}
            className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60 resize-none"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleConfirm}
            disabled={updating || !canApplySanction()}
            className="flex-1 rounded-lg border border-purple-500/40 bg-purple-500/15 py-2.5 text-sm font-semibold text-purple-200 hover:bg-purple-500/25 disabled:opacity-40 transition-colors"
          >
            {updating ? t('sanction.applying') : t('sanction.confirm')}
          </button>
          <button
            onClick={onCancel}
            disabled={updating}
            className="flex-1 rounded-lg border border-white/15 bg-black/30 py-2.5 text-sm font-semibold text-slate-200 hover:bg-black/45 disabled:opacity-40 transition-colors"
          >
            {t('modal.changeRole.cancel')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SanctionModal;
